import React from 'react';
import { Box, Grid, Paper, Typography, LinearProgress, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import { Search as SearchIcon, Notifications as NotificationsIcon } from '@mui/icons-material';

const DashboardContainer = styled(Box)(({ theme }) => ({
  background: 'radial-gradient(circle at 0% 0%, #373b52, #252736 51%, #1d1e26)',
  minHeight: '100vh',
  padding: theme.spacing(4),
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 63, 84, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
  }
}));

const ContributorDashboard: React.FC = () => {
  const metrics = {
    totalContributions: 156,
    activeProposals: 3,
    completedProjects: 12,
    reputationScore: 87,
    recentActivities: [
      { id: 1, type: 'Proposal', title: 'AI-Driven Analytics', status: 'Approved' },
      { id: 2, type: 'Project', title: 'Neural Network Enhancement', status: 'In Progress' },
      { id: 3, type: 'Contribution', title: 'Code Optimization', status: 'Completed' },
    ]
  };

  return (
    <DashboardContainer>
      <HeaderSection>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #2196f3 30%, #8a2be2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Contributor Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <NotificationsIcon sx={{ color: '#7881A1', cursor: 'pointer' }} />
          <SearchIcon sx={{ color: '#7881A1', cursor: 'pointer' }} />
        </Box>
      </HeaderSection>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <MetricCard>
                <CardContent>
                  <Typography variant="h6">Total Contributions</Typography>
                  <Typography variant="h3">{metrics.totalContributions}</Typography>
                </CardContent>
              </MetricCard>
            </Grid>
            {/* Add more metric cards */}
          </Grid>

          <Paper sx={{ mt: 4, p: 3, background: 'rgba(26, 26, 46, 0.8)' }}>
            <Typography variant="h6" gutterBottom>Recent Activity</Typography>
            <Timeline>
              {metrics.recentActivities.map((activity) => (
                <TimelineItem key={activity.id}>
                  <TimelineSeparator>
                    <TimelineDot color="primary" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="subtitle2">{activity.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activity.type} - {activity.status}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, background: 'rgba(26, 26, 46, 0.8)' }}>
            <Typography variant="h6" gutterBottom>Reputation Score</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h3" sx={{ mr: 2 }}>
                {metrics.reputationScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                /100
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={metrics.reputationScore} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Paper>
        </Grid>
      </Grid>
    </DashboardContainer>
  );
};

export default ContributorDashboard; 